import React from 'react';
import { Box, Chip, Stack } from '@mui/material';
import { tokens } from '../../theme';

export interface FilterTabOption<T extends string> {
  value: T;
  label: string;
  /** Shown as a count pill beside the label. */
  count?: number;
}

/**
 * The segmented pill control used to switch between list views
 * (Pending / Active / Completed, and so on).
 *
 * Scrolls horizontally rather than wrapping on a phone, so a five-option
 * filter never pushes the content below the fold — and the scroll is confined
 * to this strip instead of the page.
 */
export function FilterTabs<T extends string>({
  options,
  value,
  onChange,
}: {
  options: FilterTabOption<T>[];
  value: T;
  onChange: (next: T) => void;
}) {
  return (
    <Box
      role="tablist"
      sx={{
        overflowX: 'auto',
        // Hide the scrollbar but keep the scrolling; the strip is short and a
        // visible bar under four pills looks like a broken layout.
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        // Bleed to the container edge on phones so the last pill is reachable.
        mx: { xs: -0.5, sm: 0 },
        px: { xs: 0.5, sm: 0 },
      }}
    >
      <Stack direction="row" spacing={1} sx={{ width: 'max-content', py: 0.5 }}>
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <Chip
              key={option.value}
              role="tab"
              aria-selected={selected}
              clickable
              onClick={() => onChange(option.value)}
              label={
                option.count === undefined
                  ? option.label
                  : `${option.label} (${option.count})`
              }
              sx={{
                height: 38,
                px: 0.5,
                borderRadius: `${tokens.radius.full}px`,
                fontWeight: 600,
                bgcolor: selected ? 'primary.main' : 'transparent',
                color: selected ? 'primary.contrastText' : 'text.secondary',
                border: (theme) =>
                  `1px solid ${selected ? 'transparent' : theme.palette.divider}`,
                '&:hover': {
                  bgcolor: selected ? 'primary.dark' : 'action.hover',
                },
              }}
            />
          );
        })}
      </Stack>
    </Box>
  );
}
